import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewChildren,
	ChangeDetectionStrategy,
} from "@angular/core"
import { COMMON_IMPORTS } from "@app/common-imports"
import { GenFileInputComponent } from "@app/gen-inputs/gen-file-input/gen-file-input.component"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { defaultZero } from "@app/helpers"
import { Modalable } from "@app/Modalable"
import { ApiService } from "@app/services/api.service"
import { LangService } from "@app/services/lang.service"
import { ModalService } from "@app/services/modal.service"
import { StoreService } from "@app/services/store.service"
import { ValidatorService } from "@app/services/validator.service"
import { BtnExtComponent } from "@app/utils/utils.component"

const validationFields: any = {
	//field validations, control displaying field errors and ability to submit the form
	title: { not_empty: null },
	documentName: { no_file_selected: null, file_max_size: 10 * 1000 * 1000 },
}

@Component({
	selector: "app-upload-document-row",
	imports: [
		COMMON_IMPORTS,
		GenFileInputComponent,
		GenInputComponent,
		BtnExtComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div class="d-flex  justify-content-between">
			<div>
				<app-gen-file-input
					#input
					[actualObj]="sendObj"
					fileType="file"
					[errors]="errors"
					fieldName="documentName"
					title_ph_lang="choose_file"
					(changeEvent)="uplFile($event)"
				>
				</app-gen-file-input>
			</div>
			<div class="colstat-big-double">
				<app-gen-input
					[actualObj]="sendObj"
					fieldName="title"
					[errors]="errors"
					(keyupEvent)="handleChange()"
				>
				</app-gen-input>
			</div>
			<div>
				<app-btn-ext
					text="remove"
					(clickEv)="onClose.emit(true)"
				/>
			</div>
		</div>
	`,
})
export class UploadDocumentRowComponent {
	@Input() sendObj: any = null //the patient id to attach to this document
	@Output() onClose = new EventEmitter()
	@Output() onChange = new EventEmitter()
	@ViewChild("input") input: GenFileInputComponent
	errors: any = {} //will hold errors for the fields in the form
	isFormSubmittable: boolean = false //can submit (controls disabled in the submit button)

	constructor(
		public lang: LangService,
		public apiService: ApiService,
		public validator: ValidatorService,
		public store: StoreService,
		public modalService: ModalService
	) {}

	handleChange() {
		this.onChange.emit(true)
		this.validateEntireForm()
	}

	uplFile(files: FileList) {
		//on upload file - add to file field and validate
		let file: any = files ? files.item(0) : ""
		if (file) {
			if (!this.sendObj.filesObj) {
				this.sendObj.filesObj = {}
			}
			this.sendObj.title = file.name
			this.sendObj.filesObj.documentName = file
		}
		this.handleChange()
	}

	validateEntireForm() {
		//validate all fields and detrmine if can be submitted (or has errors)
		this.isFormSubmittable = this.validator.validateEntireForm(
			this.sendObj,
			this.errors,
			validationFields
		)
	}
}

@Component({
	selector: "app-upload-document",
	imports: [COMMON_IMPORTS, UploadDocumentRowComponent, BtnExtComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div
			class="d-grid gap-4"
			style="margin-block: 30px"
		>
			@for (obj of rows; track obj.id) {
				<div class="col-12">
					<app-upload-document-row
						[sendObj]="obj"
						(onClose)="handleClose(obj.id)"
						(onChange)="handleChange()"
					/>
				</div>
			}
		</div>

		<div class="modal-btns">
			<app-btn-ext
				text="add_another_upload"
				(clickEv)="addRow()"
			/>
			<app-btn-ext
				text="save_all"
				[disabled]="!isFormSubmittable"
				[isSpin]="isSaving"
				(clickEv)="save()"
			/>
			<app-btn-ext
				text="exit"
				(clickEv)="onClose.emit()"
			/>
		</div>
	`,
})
export class UploadDocumentComponent extends Modalable {
	@ViewChildren(UploadDocumentRowComponent)
	rowComponents
	@Input({ transform: defaultZero }) patient_id: number = 0 //the patient id to attach to this document
	@Input({ transform: defaultZero }) payor_id: number = 0 //the payor id to attach to this document
	@Input({ transform: defaultZero }) user_id: number = 0 //the payor id to attach to this document
	@Input({ transform: defaultZero }) followup_id: number = 0 //the followup id to attach to this document

	rows: any[] = []
	curTopId: number = 0
	isSaving: boolean = false
	isFormSubmittable: boolean = false

	ngOnInit() {
		this.addRow()
		setTimeout(() => {
			this.rowComponents.first?.input?.elementEl?.nativeElement?.click()
		})
	}
	addRow() {
		this.rows = [
			...this.rows,
			{
				id: this.curTopId,
				title: this.lang.getVal("document"),
				remarks: "",
				patient_id: this.patient_id,
				payor_id: this.payor_id,
				user_id: this.user_id,
				followup_id: this.followup_id,
				documentName: "",
			}, //the object to send
		]
		this.curTopId = this.curTopId + 1
		this.handleChange()
	}
	handleClose(num: number) {
		this.rows = this.rows.filter((it) => it.id !== num)
	}
	handleChange() {
		this.isFormSubmittable = !this.rows.find(
			(row) => !this.validator.validateEntireForm(row, {}, validationFields)
		)
	}
	async save() {
		//upload the document to the api
		if (this.isSaving) {
			return
		}
		this.isSaving = true
		for (let obj of this.rows) {
			let sendObj = { ...obj }
			delete sendObj.filesObj
			const res = await this.apiService.upload("upload_document", {
				...sendObj,
				document: obj?.filesObj?.documentName,
			})
			this.modalService.openToast(
				res.id ? "saved_successfully" : "upload_failed"
			)
			if (res.id) {
				this.store.addGenItemRow(
					"documents",
					{ ...this.store.removeDecItem(res) },
					{
						patient_id: res.patient_id,
						payor_id: res.payor_id,
						user_id: res.user_id,
					}
				) //update the store as gen item
			}
		}
		this.isSaving = false
		this.onClose.emit()
	}
}
